import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api'
import Select from 'react-select'
import Slider from 'rc-slider'
import '../../../node_modules/rc-slider/assets/index.css'

import Navbar from '../../components/navbar/navbar'
import UserNav from '../../components/navbar/user-nav'
import ListPropertyFour from '../../components/list-property-four'
import { useUser } from '../../context/UserContext'
import SEO from '../../components/SEO'
// Navbar correcto agregado
import { LoadingMessage, EmptyState, SkeletonCard } from '../../components/loading-states'
import { generateCollectionPageSchema, generateItemListSchema } from '../../utils/schema'
import { useProperties, PropertyFilters, PropertySort, Propiedad } from '../../hooks/useProperties'
import placeholderProperty from '../../assets/img/placeholder-property.svg'

// Coordenadas de Riviera Nayarit
const RIVIERA_NAYARIT = {
  lat: 20.8684,
  lng: -105.2535,
  zoom: 11
}

// Estilo del mapa
const mapContainerStyle = {
  height: '100%',
  width: '100%',
  minHeight: '600px'
}

// API Key de Google Maps
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''

// Opciones para select de tipo de propiedad
const tipoOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'Casa', label: 'Casa' },
  { value: 'Departamento', label: 'Departamento' },
  { value: 'Terreno', label: 'Terreno' },
  { value: 'Local Comercial', label: 'Local Comercial' },
  { value: 'Villa', label: 'Villa' },
  { value: 'Condominio', label: 'Condominio' }
]

// Opciones para operación
const operacionOptions = [
  { value: 'todos', label: 'Venta y Renta' },
  { value: 'venta', label: 'En Venta' },
  { value: 'renta', label: 'En Renta' }
]

// Opciones de recámaras
const recamarasOptions = [
  { value: '', label: 'Cualquiera' },
  { value: '1', label: '1+' },
  { value: '2', label: '2+' },
  { value: '3', label: '3+' },
  { value: '4', label: '4+' },
  { value: '5', label: '5+' }
]

export default function HalfMap() {
  const { t } = useTranslation()
  const { user } = useUser()

  // Hook de propiedades
  const {
    propiedades,
    loading,
    error,
    pagination,
    fetchPropiedades,
    cargarMas,
    formatearPrecio,
    getCoordenadas
  } = useProperties()

  // Estado de filtros
  const [filters, setFilters] = useState<PropertyFilters>({
    tipo_operacion: 'todos',
    tipo_propiedad: '',
    busqueda: '',
    recamaras_min: undefined,
    banos_min: undefined,
    precio_min: undefined,
    precio_max: undefined
  })

  // Estado del mapa
  const [mapCenter, setMapCenter] = useState(RIVIERA_NAYARIT)
  const [selectedProperty, setSelectedProperty] = useState<Propiedad | null>(null)
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null)

  // Estado del panel de filtros
  const [showFilters, setShowFilters] = useState(false)

  // Estado del slider de precio
  const [priceRange, setPriceRange] = useState<number[]>([0, 5000000])

  // Cargar propiedades al montar
  useEffect(() => {
    fetchPropiedades(filters, { campo: 'fecha_creacion', direccion: 'desc' }, true)
  }, [])

  // Generar markers de propiedades con coordenadas
  const markers = useMemo(() => {
    return propiedades
      .map(prop => {
        const coords = getCoordenadas(prop)
        if (!coords) return null
        return {
          id: prop.id,
          position: coords,
          propiedad: prop
        }
      })
      .filter(Boolean) as { id: string; position: { lat: number; lng: number }; propiedad: Propiedad }[]
  }, [propiedades, getCoordenadas])

  // Manejar búsqueda
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    fetchPropiedades(filters, { campo: 'fecha_creacion', direccion: 'desc' }, true)
  }, [filters, fetchPropiedades])

  // Manejar cambio de filtros
  const handleFilterChange = (key: keyof PropertyFilters, value: any) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
  }

  // Aplicar filtros
  const handleApplyFilters = () => {
    const newFilters = {
      ...filters,
      precio_min: priceRange[0] > 0 ? priceRange[0] : undefined,
      precio_max: priceRange[1] < 5000000 ? priceRange[1] : undefined
    }
    setFilters(newFilters)
    fetchPropiedades(newFilters, { campo: 'fecha_creacion', direccion: 'desc' }, true)
    setShowFilters(false)
  }

  // Resetear filtros
  const handleResetFilters = () => {
    const defaultFilters: PropertyFilters = {
      tipo_operacion: 'todos',
      tipo_propiedad: '',
      busqueda: '',
      recamaras_min: undefined,
      banos_min: undefined,
      precio_min: undefined,
      precio_max: undefined
    }
    setFilters(defaultFilters)
    setPriceRange([0, 5000000])
    fetchPropiedades(defaultFilters, { campo: 'fecha_creacion', direccion: 'desc' }, true)
  }

  // Manejar clic en marker
  const handleMarkerClick = (propiedad: Propiedad) => {
    setSelectedProperty(propiedad)
    const coords = getCoordenadas(propiedad)
    if (coords) {
      setMapCenter({ ...coords, zoom: 14 })
    }
  }

  // Convertir propiedad a formato del componente ListPropertyFour
  const mapPropiedadToItem = (prop: Propiedad) => {
    // Preparar imágenes (debe ser array para ListPropertyFour)
    let images: string[] = []
    if (prop.fotos && Array.isArray(prop.fotos) && prop.fotos.length > 0) {
      images = prop.fotos
    } else if (prop.imagen) {
      images = [prop.imagen]
    } else {
      images = [placeholderProperty]
    }

    return {
      id: prop.id,
      name: prop.nombre || prop.titulo || 'Propiedad sin nombre',
      image: images,
      gallery: prop.fotos || [],
      value: formatearPrecio(prop.precio_actual_usd || prop.precio || 0),
      loction: prop.zona || prop.area || prop.ciudad || (typeof prop.direccion === 'string' ? prop.direccion : prop.direccion?.zona || prop.direccion?.area || prop.direccion?.ciudad) || '',
      beds: `${prop.recamaras || 0} Rec`,
      size: `${prop.banos || 0} Baños`,
      sqft: `${prop.tamano_construccion || prop.tamano_terreno || 0} m²`,
      tag2: prop.tipo_operacion === 'renta' ? 'For Rent' : 'For Sell',
      propertyType: prop.tipo_propiedad || prop.tipo || ''
    }
  }

  // Schema.org para SEO
  const collectionSchema = generateCollectionPageSchema(
    'Propiedades con Mapa - Riviera Nayarit',
    'Explora propiedades con vista de mapa interactivo en Riviera Nayarit',
    '/half-map'
  )

  const itemListSchema = generateItemListSchema(
    propiedades.slice(0, 10).map(item => ({
      id: item.id,
      name: item.nombre || item.titulo || 'Propiedad',
      url: `/single-property-1/${item.id}`,
      image: item.imagen || (item.fotos && item.fotos[0]) || ''
    })),
    'Propiedades con Mapa',
    'Propiedades mostradas en mapa interactivo'
  )

  return (
    <>
      <SEO
        title="Propiedades con Mapa | AMPI Riviera Nayarit"
        description="Explora propiedades con vista de mapa interactivo en Riviera Nayarit. Encuentra casas, departamentos y terrenos ubicados estratégicamente."
        path="/half-map"
        type="website"
        schema={[collectionSchema, itemListSchema]}
      />
      {user ? (
        <UserNav nombreUsuario={user.nombre} nombreInmobiliaria={user.inmobiliaria_nombre} />
      ) : (
        <Navbar transparent={false} />
      )}

      <div className="half-map-container">
        {/* Mapa móvil (visible solo en móvil) */}
        <div className="d-md-none mb-3" style={{ height: '300px' }}>
          {GOOGLE_MAPS_API_KEY ? (
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                mapContainerStyle={{ height: '100%', width: '100%' }}
                center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                zoom={mapCenter.zoom || RIVIERA_NAYARIT.zoom}
              >
                {markers.map(marker => (
                  <Marker
                    key={marker.id}
                    position={marker.position}
                    onClick={() => handleMarkerClick(marker.propiedad)}
                    icon={hoveredPropertyId === marker.id ? {
                      url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                    } : undefined}
                  />
                ))}
              </GoogleMap>
            </LoadScript>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 bg-light">
              <div className="text-center text-muted">
                <i className="fa-solid fa-map-location-dot fa-3x mb-3"></i>
                <p>Configure REACT_APP_GOOGLE_MAPS_API_KEY en .env</p>
              </div>
            </div>
          )}
        </div>

        <div className="row g-0">
          {/* Panel del mapa (desktop) */}
          <div className="col-md-5 d-none d-md-block position-fixed" style={{ height: 'calc(100vh - 80px)', top: '80px' }}>
            {GOOGLE_MAPS_API_KEY ? (
              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
                  zoom={mapCenter.zoom || RIVIERA_NAYARIT.zoom}
                >
                  {markers.map(marker => (
                    <Marker
                      key={marker.id}
                      position={marker.position}
                      onClick={() => handleMarkerClick(marker.propiedad)}
                      icon={hoveredPropertyId === marker.id ? {
                        url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                      } : undefined}
                    />
                  ))}

                  {/* InfoWindow para propiedad seleccionada */}
                  {selectedProperty && getCoordenadas(selectedProperty) && (
                    <InfoWindow
                      position={getCoordenadas(selectedProperty)!}
                      onCloseClick={() => setSelectedProperty(null)}
                    >
                      <div style={{ maxWidth: '250px' }}>
                        <img
                          src={selectedProperty.imagen || (selectedProperty.fotos && selectedProperty.fotos[0]) || placeholderProperty}
                          alt={selectedProperty.nombre || 'Propiedad'}
                          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <h6 className="mt-2 mb-1">{selectedProperty.nombre || selectedProperty.titulo}</h6>
                        <p className="text-primary fw-bold mb-1">
                          {formatearPrecio(selectedProperty.precio_actual_usd || selectedProperty.precio || 0)}
                        </p>
                        <p className="small text-muted mb-2">
                          {selectedProperty.zona || selectedProperty.area || selectedProperty.ciudad || 'Riviera Nayarit'}
                        </p>
                        <Link
                          to={`/single-property-1/${selectedProperty.id}`}
                          className="btn btn-sm btn-primary w-100"
                        >
                          Ver detalles
                        </Link>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </LoadScript>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                <div className="text-center text-muted p-4">
                  <i className="fa-solid fa-map-location-dot fa-4x mb-3"></i>
                  <h5>Mapa no disponible</h5>
                  <p className="mb-3">Configure la API key de Google Maps en el archivo .env</p>
                  <code className="bg-dark text-light p-2 rounded d-block">
                    REACT_APP_GOOGLE_MAPS_API_KEY=tu_api_key
                  </code>
                </div>
              </div>
            )}

            {/* Indicador de propiedades en mapa */}
            <div className="position-absolute bottom-0 start-0 m-3 bg-white rounded shadow p-2">
              <small className="text-muted">
                <i className="fa-solid fa-location-dot text-primary me-1"></i>
                {markers.length} propiedades en el mapa
              </small>
            </div>
          </div>

          {/* Panel de listado */}
          <div className="col-md-7 offset-md-5">
            <div className="p-4">
              {/* Barra de búsqueda y filtros */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-3">
                    <h4 className="mb-0">
                      <i className="fa-solid fa-building me-2 text-primary"></i>
                      Propiedades en Riviera Nayarit
                    </h4>
                    <div className="d-flex align-items-center gap-3">
                      <span className="badge bg-primary fs-6">
                        {pagination.total} encontradas
                      </span>
                      <ul className="shorting-list list-unstyled d-flex gap-2 mb-0">
                        <li>
                          <Link to="/grid">
                            <span className="svg-icon text-muted-2 svg-icon-2hx">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="2" width="9" height="9" rx="2" fill="currentColor"/>
                                <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="currentColor"/>
                                <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="currentColor"/>
                                <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="currentColor"/>
                              </svg>
                            </span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/classical-layout-with-sidebar">
                            <span className="svg-icon text-muted-2 svg-icon-2hx">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M14 10V20C14 20.6 13.6 21 13 21H10C9.4 21 9 20.6 9 20V10C9 9.4 9.4 9 10 9H13C13.6 9 14 9.4 14 10ZM20 9H17C16.4 9 16 9.4 16 10V20C16 20.6 16.4 21 17 21H20C20.6 21 21 20.6 21 20V10C21 9.4 20.6 9 20 9Z" fill="currentColor"/>
                                <path d="M7 10V20C7 20.6 6.6 21 6 21H3C2.4 21 2 20.6 2 20V10C2 9.4 2.4 9 3 9H6C6.6 9 7 9.4 7 10ZM21 6V3C21 2.4 20.6 2 20 2H3C2.4 2 2 2.4 2 3V6C2 6.6 2.4 7 3 7H20C20.6 7 21 6.6 21 6Z" fill="currentColor"/>
                              </svg>
                            </span>
                          </Link>
                        </li>
                        <li>
                          <Link to="/half-map" className="active">
                            <span className="svg-icon text-primary svg-icon-2hx">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path opacity="0.3" d="M18.0624 15.3453L13.1624 20.7453C12.5624 21.4453 11.5624 21.4453 10.9624 20.7453L6.06242 15.3453C4.56242 13.6453 3.76242 11.4453 4.06242 8.94534C4.56242 5.34534 7.46242 2.44534 11.0624 2.04534C15.8624 1.54534 19.9624 5.24534 19.9624 9.94534C20.0624 12.0453 19.2624 13.9453 18.0624 15.3453Z" fill="currentColor"/>
                                <path d="M12.0624 13.0453C13.7193 13.0453 15.0624 11.7022 15.0624 10.0453C15.0624 8.38849 13.7193 7.04535 12.0624 7.04535C10.4056 7.04535 9.06241 8.38849 9.06241 10.0453C9.06241 11.7022 10.4056 13.0453 12.0624 13.0453Z" fill="currentColor"/>
                              </svg>
                            </span>
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <form onSubmit={handleSearch}>
                    <div className="input-group mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por ubicación, nombre..."
                        value={filters.busqueda}
                        onChange={(e) => handleFilterChange('busqueda', e.target.value)}
                        aria-label="Buscar propiedades"
                      />
                      <button type="submit" className="btn btn-primary" aria-label="Buscar">
                        <i className="fa-solid fa-search"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary position-relative"
                        onClick={() => setShowFilters(!showFilters)}
                        aria-expanded={showFilters}
                        aria-label="Mostrar filtros"
                      >
                        <i className="fa-solid fa-sliders"></i>
                        {(filters.tipo_propiedad || filters.tipo_operacion !== 'todos' || filters.recamaras_min) && (
                          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                            !
                          </span>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Panel de filtros expandible */}
                  {showFilters && (
                    <div className="card mb-4">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label small fw-bold">Tipo de Operación</label>
                            <Select
                              options={operacionOptions}
                              value={operacionOptions.find(o => o.value === filters.tipo_operacion)}
                              onChange={(opt) => handleFilterChange('tipo_operacion', opt?.value || 'todos')}
                              placeholder="Seleccionar..."
                              classNamePrefix="select"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small fw-bold">Tipo de Propiedad</label>
                            <Select
                              options={tipoOptions}
                              value={tipoOptions.find(o => o.value === filters.tipo_propiedad)}
                              onChange={(opt) => handleFilterChange('tipo_propiedad', opt?.value || '')}
                              placeholder="Seleccionar..."
                              classNamePrefix="select"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small fw-bold">Recámaras mínimas</label>
                            <Select
                              options={recamarasOptions}
                              value={recamarasOptions.find(o => o.value === String(filters.recamaras_min || ''))}
                              onChange={(opt) => handleFilterChange('recamaras_min', opt?.value ? Number(opt.value) : undefined)}
                              placeholder="Cualquiera"
                              classNamePrefix="select"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small fw-bold">Baños mínimos</label>
                            <Select
                              options={recamarasOptions}
                              value={recamarasOptions.find(o => o.value === String(filters.banos_min || ''))}
                              onChange={(opt) => handleFilterChange('banos_min', opt?.value ? Number(opt.value) : undefined)}
                              placeholder="Cualquiera"
                              classNamePrefix="select"
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label small fw-bold">
                              Rango de Precio (USD): {formatearPrecio(priceRange[0])} - {formatearPrecio(priceRange[1])}
                            </label>
                            <Slider
                              range
                              min={0}
                              max={5000000}
                              step={50000}
                              value={priceRange}
                              onChange={(values) => Array.isArray(values) && setPriceRange(values)}
                              allowCross={false}
                            />
                          </div>
                          <div className="col-12">
                            <div className="d-flex gap-2 justify-content-end">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleResetFilters}
                              >
                                <i className="fa-solid fa-rotate-left me-1"></i>
                                Resetear
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleApplyFilters}
                              >
                                <i className="fa-solid fa-check me-1"></i>
                                Aplicar Filtros
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Listado de propiedades */}
              {loading && propiedades.length === 0 ? (
                <div className="row g-3">
                  {[1, 2, 3, 4].map(i => (
                    <div className="col-12" key={i}>
                      <SkeletonCard variant="property" />
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  <i className="fa-solid fa-exclamation-triangle me-2"></i>
                  {error}
                  <button
                    className="btn btn-link"
                    onClick={() => fetchPropiedades(filters, { campo: 'fecha_creacion', direccion: 'desc' }, true)}
                  >
                    Reintentar
                  </button>
                </div>
              ) : propiedades.length === 0 ? (
                <EmptyState
                  icon="fa-building"
                  title="No se encontraron propiedades"
                  message="Intenta ajustar los filtros de búsqueda"
                  actionText="Limpiar filtros"
                  onAction={handleResetFilters}
                />
              ) : (
                <>
                  <div className="row g-3 list-layout">
                    {propiedades.map((prop) => (
                      <div
                        className="col-12"
                        key={prop.id}
                        onMouseEnter={() => setHoveredPropertyId(prop.id)}
                        onMouseLeave={() => setHoveredPropertyId(null)}
                      >
                        <ListPropertyFour item={mapPropiedadToItem(prop)} border={true} />
                      </div>
                    ))}
                  </div>

                  {/* Cargar más */}
                  {pagination.pagina < pagination.totalPaginas && (
                    <div className="text-center mt-4">
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={cargarMas}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Cargando...
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-plus me-2"></i>
                            Cargar más propiedades
                          </>
                        )}
                      </button>
                      <p className="text-muted mt-2 small">
                        Mostrando {propiedades.length} de {pagination.total}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .half-map-container {
          min-height: calc(100vh - 80px);
        }
        @media (max-width: 767px) {
          .half-map-container .col-md-7 {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </>
  )
}
